import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import product from '@/data/product'
import all from '@/data/all'
import { hasIntersection, isAsciiOrExtended } from '@/utils'
import { ATTRIBUTE_KEY, ProductCategoryValue } from '@/data/const'

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const parseJSON = <T = any>(json: string | undefined, def: T): T => {
  try {
    if (typeof json === 'string') return JSON.parse(json)
    throw 'json must be a string'
  } catch (error) {
    return def
  }
}

export type RawProductConfig = (typeof product.RING)[number]
export type FormattedProduct = ReturnType<typeof formatProduct>
const formatProduct = (product: RawProductConfig) => {
  const current = all.find(item => item.skuId === product.skuId)
  const pdpTitles: string[] = parseJSON(current?.pdpTitle, [])

  return {
    ...product,
    price: current?.price || 0,
    titleString: current?.title,
    title: pdpTitles.map(text => ({
      text,
      isAsciiOrExtended: isAsciiOrExtended(text)
    }))
  }
}

export const useProductStore = defineStore('product', () => {
  const products = ref<FormattedProduct[]>([])

  const selectedCategory = ref<ProductCategoryValue>()
  const selectedAttributes = ref<ATTRIBUTE_KEY[]>([])
  const selectedProduct = ref<FormattedProduct>()

  const selectionSource = ref<string>('')
  const isAllWorksCat = computed(() =>
    selectionSource.value.startsWith('allworks')
  )
  const isQueryEmpty = computed(
    () => !selectedCategory.value && selectedAttributes.value.length === 0
  )
  const filteredProducts = computed(() => {
    if (isQueryEmpty.value) return []
    // console.log('[[ Selection ] Filter Product ]', Date.now())
    const list = products.value
      .filter(
        selectedCategory.value
          ? selectedAttributes.value.length
            ? product =>
                product.category.value === selectedCategory.value &&
                hasIntersection(
                  selectedAttributes.value,
                  (product.attributes || []).map(attr => attr.label)
                )
            : product => product.category.value === selectedCategory.value
          : selectedAttributes.value.length
          ? product =>
              hasIntersection(
                selectedAttributes.value,
                (product.attributes || []).map(attr => attr.label)
              )
          : (console.error(
              '[[ Selection ] Filter Product Error ]',
              `出现无条件过滤`,
              Date.now()
            ),
            () => true)
      )
      .sort((a, b) => {
        if (isAllWorksCat.value) return a.orderInAll - b.orderInAll
        return a.orderOfNew - b.orderOfNew
      })
    return list
  })

  const initProducts = () => {
    const list = [
      ...product.WATCH,
      ...product.RING,
      ...product.BRACELETS,
      ...product.EARRINGS
    ]
      .map(formatProduct)
      .filter(product => product.title)
    products.value = list
  }

  const setSelectedCategory = (
    category: ProductCategoryValue,
    source: string
  ) => {
    // console.log(
    //   '[[ Selection ] try to select category ]',
    //   {
    //     category: [category, selectedCategory.value || '-'],
    //     source: [source, selectionSource.value || '-']
    //   },
    //   Date.now()
    // )
    if (
      category === selectedCategory.value &&
      source === selectionSource.value
    ) {
      return false
    }
    console.log(
      '[[ Selection ] Set Selected Category ]',
      {
        category: [category, selectedCategory.value || '-']
      },
      Date.now()
    )
    selectedProduct.value = undefined
    selectedCategory.value = category
    selectionSource.value = source
    return true
  }

  const setSelectedAttributes = (attrs: ATTRIBUTE_KEY[]) => {
    // console.log(
    //   '[[ Selection ] try to select attributes ]',
    //   {
    //     attrs
    //   },
    //   Date.now()
    // )
    if (selectedAttributes.value.length === attrs.length) {
      if (attrs.length === 0) {
        return false
      }
      if (selectedAttributes.value.every(item => attrs.includes(item))) {
        return false
      }
    }
    console.log(
      '[[ Selection ] Select Attributes ]',
      {
        attrs
      },
      Date.now()
    )
    selectedProduct.value = undefined
    selectedAttributes.value = attrs
    return true
  }

  const setSelectedProduct = (product: FormattedProduct) => {
    // console.log(
    //   '[[ Selection ] try to select product ]',
    //   {
    //     skuId: product.skuId
    //   },
    //   Date.now()
    // )
    if (product?.skuId === selectedProduct.value?.skuId) return false
    console.log(
      '[[ Selection ] Set Selected Product ]',
      {
        skuId: product.skuId
      },
      Date.now()
    )
    selectedProduct.value = product
    return true
  }

  const resetPartial = () => {
    let changed = false
    if ((selectedAttributes.value?.length || 0) > 0) {
      // console.log(
      //   '[[ Selection ] Reset ProductStore / Attributes ]',
      //   Date.now()
      // )
      selectedAttributes.value = []
      changed = true
    }
    if (selectedProduct.value !== undefined) {
      // console.log('[[ Selection ] Reset ProductStore / Product ]', Date.now())
      selectedProduct.value = undefined
      changed = true
    }
    selectionSource.value = ''
    return changed
  }

  const resetSelectedCategory = (source: string) => {
    let changed = false
    selectionSource.value = source
    if (selectedProduct.value !== undefined) {
      // console.log('[[ Selection ] Reset ProductStore / Product ]', Date.now())
      selectedProduct.value = undefined
      changed = true
    }
    if (selectedCategory.value !== undefined) {
      // console.log('[[ Selection ] Reset ProductStore / Category ]', Date.now())
      selectedCategory.value = undefined
      changed = true
    }
    return changed
  }

  const reset = () => {
    // console.log(
    //   '[[ Selection ] try to reset product store ]',
    //   {
    //     category: selectedCategory.value || '-',
    //     isAllWorksCat: isAllWorksCat.value || '-'
    //   },
    //   Date.now()
    // )
    let changed = resetPartial()
    if (resetSelectedCategory('')) {
      changed = true
    }
    return changed
  }

  return {
    isAllWorksCat,
    isQueryEmpty,
    products,
    selectionSource,
    selectedCategory,
    selectedAttributes,
    selectedProduct,
    filteredProducts,
    initProducts,
    setSelectedCategory,
    setSelectedAttributes,
    setSelectedProduct,
    resetPartial,
    resetSelectedCategory,
    reset
  }
})
